import {useIntl} from 'react-intl'
import {CreateFields} from '../../types/forms'
import {MAX_FIELD_LENGTH, POSTCODE_REGEXP, ADDRESS_REGEXP, FIRST_NAME_REGEXP, LAST_NAME_REGEXP, MAX_QUESTION_ANSWER_LENGTH} from '../../utils/constants'
import {validateEmail} from '../../utils/email-utils'
import {validatePhoneNumber} from '../../utils/phone-utils'
import { CompetitionMarketingFormFields } from '../../types/cms/competition-marketing-form'
import { formatPostcode } from './useCheckPostcodeFields'

export const useCompetitionMarketingFormFields: CreateFields<CompetitionMarketingFormFields> = ({
  form: {
    control,
    formState: {errors},
    getValues
  },
  questionnaire
}) => {
  const {formatMessage} = useIntl()
  const question1Max = questionnaire?.question1MaxLength || MAX_QUESTION_ANSWER_LENGTH
  const question2Max = questionnaire?.question2MaxLength || MAX_QUESTION_ANSWER_LENGTH
  const question3Max = questionnaire?.question3MaxLength || MAX_QUESTION_ANSWER_LENGTH

  const fields = {
    question1: {
      name: `question1`,
      label: formatMessage({defaultMessage: 'Answer'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: question1Max,
          message: `Answer cannot be longer than ${question1Max} characters`,
        },
      },
      inputProps: {
        'data-test-selector': 'question-one',
      },
      error: errors[`question1`],
      control,
    },
    question2: {
      name: `question2`,
      label: formatMessage({defaultMessage: 'Answer'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: question2Max,
          message: `Answer cannot be longer than ${question2Max} characters`,
        },
      },
      inputProps: {
        'data-test-selector': 'question-two',
      },
      error: errors[`question2`],
      control,
    },
    question3: {
      name: `question3`,
      label: formatMessage({defaultMessage: 'Answer'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: question3Max,
          message: `Answer cannot be longer than ${question3Max} characters`,
        },
      },
      inputProps: {
        'data-test-selector': 'question-three',
      },
      error: errors[`question3`],
      control,
    },
    firstName: {
      name: `firstname`,
      label: formatMessage({defaultMessage: 'First Name'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (value: string) => {
          const trimmedValue = value.trim()
          return (
            trimmedValue.length > 0 &&
            FIRST_NAME_REGEXP.test(trimmedValue)
          ) || formatMessage({ defaultMessage: 'Please enter a valid name.' })
        },
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `First Name cannot be longer than ${MAX_FIELD_LENGTH} characters`,
        },
      },
      inputProps: {
        'data-test-selector': 'first-name-field',
      },
      error: errors[`firstname`],
      control,
    },
    lastName: {
      name: `lastname`,
      label: formatMessage({defaultMessage: 'Last Name'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (value: string) => {
          const trimmedValue = value.trim()
          return (
            trimmedValue.length > 0 &&
            LAST_NAME_REGEXP.test(trimmedValue)
          ) || formatMessage({ defaultMessage: 'Please enter a valid name.' })
        },
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `Last Name cannot be longer than ${MAX_FIELD_LENGTH} characters`,
        },
      },
      inputProps: {
        'data-test-selector': 'last-name-field',
      },
      error: errors[`lastname`],
      control,
    },
    email: {
      name: `email`,
      label: formatMessage({defaultMessage: 'Email address'}),
      defaultValue: '',
      type: 'email' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `Email cannot be longer than ${MAX_FIELD_LENGTH} characters.`,
        },
        validate: (value: string) =>
          validateEmail(value).isValid ||
          formatMessage({defaultMessage: 'Please enter a valid email address.'}),
      },
      inputProps: {
        // For login fields browser still tries to autofill even if autocomplete is off... So setting `new-password` to fix it.
        // See: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
        autoComplete: 'new-password',
        maxLength: MAX_FIELD_LENGTH,
        'data-test-selector': 'email-field'
      },
      error: errors[`email`],
      control,
    },
    phone: {
      name: `phone`,
      label: formatMessage({defaultMessage: 'Phone Number'}),
      defaultValue: '',
      type: 'tel' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (phone: string) =>
          validatePhoneNumber(phone).isValid ||
          formatMessage({defaultMessage: 'Please specify a valid phone number.'}),
      },
      inputProps: {
        'data-test-selector': 'phone-field'
      },
      error: errors[`phone`],
      control,
    },
    address: {
      name: `address`,
      label: formatMessage({defaultMessage: 'Enter your postcode'}),
      defaultValue: '',
      type: 'text' as const,
      inputProps: {
        'data-test-selector': 'address-field'
      },
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: () => {
          const values = getValues()
          const hasAddress1 = values.address1 && values.address1.trim().length > 0
          const hasPostalCode = values.postalCode && values.postalCode.trim().length > 0
          
          if (!hasAddress1 || !hasPostalCode) {
            return formatMessage({defaultMessage: 'Please select an address from the dropdown'})
          }
          return true
        }
      },
      error: errors[`address`],
      control,
    },
    marketingPermission1: {
      name: `marketingPermission1`,
      label: '',
      defaultValue: false,
      type: 'checkbox' as const,
      inputProps: {
        'data-test-selector': 'marketing-permission-1-field'
      },
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (value: boolean) => value === true || formatMessage({defaultMessage: 'This field is required.'})
      },
      error: errors[`marketingPermission1`],
      control,
    },
    marketingPermission2: {
      name: `marketingPermission2`,
      label: '',
      defaultValue: false,
      type: 'checkbox' as const,
      inputProps: {
        'data-test-selector': 'marketing-permission-2-field'
      },
      error: errors[`marketingPermission2`],
      control,
    },
    searchForAddress: {
      name: `searchForAddress` as const,
      label: '',
      defaultValue: '',
      type: 'text' as const,
      error: errors[`searchForAddress`],
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (postcode: string) => {
          const formatted = formatPostcode(postcode)
          return POSTCODE_REGEXP.test(formatted) || 'Please specify a valid postal code'
        }
      },
      control,
    },
    address1: {
      name: `address1`,
      label: formatMessage({defaultMessage: 'Address line 1'}),
      defaultValue: '',
      type: 'text' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        pattern: {
          value: ADDRESS_REGEXP,
          message: 'Please enter a valid value',
        },
      },
      error: errors[`address1`],
      control,
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'address-line-1-field'
      },
    },
    address2: {
      name: `address2`,
      label: formatMessage({defaultMessage: 'Address line 2'}),
      rules: {
        pattern: {
          value: ADDRESS_REGEXP,
          message: formatMessage({defaultMessage: 'Please enter a valid value'}),
        },
      },
      defaultValue: '',
      type: 'text' as const,
      error: errors[`address2`],
      control,
      inputProps: {
        'data-test-selector': 'address-line-2-field'
      },
    },
    city: {
      name: `city`,
      label: formatMessage({defaultMessage: 'Town or City'}),
      defaultValue: '',
      type: 'text' as const,
      rules: {
        pattern: {
          value: ADDRESS_REGEXP,
          message: formatMessage({defaultMessage: 'Please enter a valid value'}),
        },
        required: formatMessage({defaultMessage: 'This field is required.'}),
      },
      error: errors[`city`],
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'address-city-field'
      },
      control,
    },
    postcode: {
      name: `postalCode`,
      label: formatMessage({defaultMessage: 'Postcode'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (postcode: string) => {
          const formatted = formatPostcode(postcode)
          return POSTCODE_REGEXP.test(formatted) || 'Please specify a valid postal code'
        }
      },
      error: errors[`postalCode`],
      control,
    },
  }
  return fields
}